import { useDraggable } from '@dnd-kit/core';
import { type ReactElement } from 'react';

import { getConversationPreview } from '@apps/web/components/shared/messages/conversation_list_utils';
import {
    formatTypingLabel,
    useConversationTyping,
} from '@apps/web/hooks/use_conversation_typing';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import { formatDateAndTime } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type OrganizationConversationMetadata } from '@libraries/typescript/api_client';

type ConversationBoardCardProps = {
    readonly conversation: OrganizationConversationMetadata;
    readonly organizationId: string;
    readonly currentUserId: string | null;
    readonly timezone: string;
    readonly isSelected: boolean;
    readonly onSelect: () => void;
};

export function ConversationBoardCard({
    conversation,
    organizationId,
    currentUserId,
    timezone,
    isSelected,
    onSelect,
}: ConversationBoardCardProps): ReactElement {
    const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
        id: conversation.id,
    });
    const { formatName } = useFormatPersonName();
    const preview = getConversationPreview(conversation);
    const title =
        (conversation.caregiver_name
            ? formatName(conversation.caregiver_name)
            : null) ||
        conversation.external_contact_name ||
        conversation.phone_number;

    // Board cards show live typing presence so an operator scanning the board
    // can see when a caregiver is actively replying.
    const { typingUsers } = useConversationTyping({
        organizationId,
        conversationId: conversation.id,
        currentUserId,
        enabled: true,
    });
    const typingLabel = formatTypingLabel(typingUsers);

    return (
        <div
            ref={setNodeRef}
            className={cn(
                'group bg-card hover:border-primary/40 relative flex cursor-grab flex-col gap-2 rounded-lg border p-3 text-left shadow-sm transition-colors active:cursor-grabbing',
                isSelected && 'border-primary ring-primary/20 ring-2',
                isDragging && 'opacity-40',
            )}
            {...attributes}
            {...listeners}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(keyEvent) => {
                if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
                    keyEvent.preventDefault();
                    onSelect();
                }
            }}
        >
            <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {title}
                </span>
            </div>
            {typingLabel ? (
                <p className="text-xs font-medium text-blue-600">
                    {typingLabel}
                </p>
            ) : preview ? (
                <p className="text-muted-foreground line-clamp-2 text-xs">
                    {preview.text}
                </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-muted-foreground text-[11px]">
                    {formatDateAndTime(conversation.last_active_at, timezone)}
                </span>
            </div>
        </div>
    );
}
